import { captureException } from "@sentry/browser";
import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";

import { IProduct } from "../../../models/catalogue";
import {
    IImageURL,
    IProductID,
    ISafeHTML,
    ProductIDs,
    WebPageURL,
} from "../../../models/nominals";
import { check } from "../../../models/utils";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    trackListrackProductBrowse,
    trackVariantSelectedEvent,
} from "../../../utils/analytics";
import { defaults } from "../defaults";
import {
    getSelectedUpgradeIDFromURL,
    pushPDPVariantURLChange,
    startHistoryListener,
} from "../history";
import {
    IPLCOptionPanel,
    IPLCProductCategorySelector,
    IUpsellModalComponentFn,
    PLCOptionPanels,
    PLCProductCategorySelectors,
} from "../models";
import {
    addonVariantPricesSelector,
    baseVariantSelector,
    rootProductSelector,
    upgradedVariantPriceSelector,
    upgradedVariantSelector,
} from "../selectors";
import { loadConcreteBundles, loadProducts } from "../thunks";
import { PLCConfigurator as PLCConfiguratorComponent } from "./PLCConfiguratorContent";

const parseCategorySelectors = (
    categorySelectorsJSON: string,
): readonly IPLCProductCategorySelector[] => {
    const selectors = check(
        PLCProductCategorySelectors.decode(JSON.parse(categorySelectorsJSON)),
    );
    return selectors;
};

const parseProducts = (productsJSON: string): ReadonlyArray<IProductID> => {
    const products = check(ProductIDs.decode(JSON.parse(productsJSON)));
    return products;
};

const parseOptionPanels = (
    optionPanelsJSON: string,
): readonly IPLCOptionPanel[] => {
    const panels = check(PLCOptionPanels.decode(JSON.parse(optionPanelsJSON)));
    return panels;
};

export const PLCConfigurator = (
    props: Immutable<{
        preheader?: string;
        title: string;
        subhead?: string;
        description?: string;
        footer: ISafeHTML;
        categorySelectors: string;
        products: string;
        optionPanels: string;
        starRatingURL: WebPageURL;
        basketLink: WebPageURL;
        configureGiftsLink: WebPageURL;
        showShippingLead?: boolean;
        phoneNumber?: string;
        getDeliveryCopy?: (
            rootProduct: IProduct,
            isPreorder: boolean,
        ) => string | null;
        getDeliveryIcon?: () => IImageURL | null;
        // Root Product Variant ID which should be initially selected
        initialVariantID: IProductID | null;
        // Behavior options
        enableHistoryInteraction: boolean;
        // Optional: Up Sell Modal functionality
        showUpsellModal?: (product: IProduct | null) => boolean;
        getUpsellModalComponentFn?: (
            product: IProduct,
        ) => IUpsellModalComponentFn | null;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const rootProduct = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return rootProductSelector(state);
    });

    const baseVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const selectedUpgradeID = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.selectedUpgrade;
    });

    const upgradedVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return upgradedVariantSelector(state);
    });

    const price = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return upgradedVariantPriceSelector(state);
    });

    const priceError = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.priceError;
    });

    const selectedAddonPrice = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return addonVariantPricesSelector(state);
    });

    const shippingMethod = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.shippingMethod;
    });

    const categorySelectors = parseCategorySelectors(props.categorySelectors);

    const rootProductIDs = parseProducts(props.products);

    const optionPanels = parseOptionPanels(props.optionPanels);

    // Track previous values for componentDidUpdate logic
    const prevBaseVariantIDRef = useRef<IProductID | null>(null);
    const prevUpgradedVariantIDRef = useRef<IProductID | null>(null);

    // componentDidMount
    useEffect(() => {
        if (props.enableHistoryInteraction) {
            startHistoryListener(dispatch);
        }

        // Load main product from API. Under full-document
        // hydrateRoot(document) there is no per-island error boundary, so
        // throwing here would blank the whole page; on an empty product set we
        // report and skip the load instead.
        if (rootProductIDs.length <= 0) {
            captureException(
                new Error(
                    "PLCConfigurator rootProductIDs is empty; skipping product load",
                ),
                {
                    extra: {
                        rootProductIDs: rootProductIDs,
                        initialVariantID: props.initialVariantID,
                    },
                },
            );
        } else {
            dispatch(
                loadProducts({
                    productIDs: rootProductIDs,
                    initialVariantID: props.initialVariantID,
                    categorySelectors: categorySelectors,
                    optionPanels: optionPanels,
                }),
            );
        }
    }, []);

    // componentDidUpdate - track variant changes
    useEffect(() => {
        const prevBaseVariantID = prevBaseVariantIDRef.current;
        const nextBaseVariantID =
            baseVariant && baseVariant.id ? baseVariant.id : null;
        const baseVariantChanged = prevBaseVariantID !== nextBaseVariantID;

        const prevUpgradedVariantID = prevUpgradedVariantIDRef.current;
        const nextUpgradedVariantID =
            upgradedVariant && upgradedVariant.id ? upgradedVariant.id : null;
        const upgradedVariantChanged =
            prevUpgradedVariantID !== nextUpgradedVariantID;

        // Load bundle data for the new variant
        if (baseVariantChanged && nextBaseVariantID) {
            const selectedUpgradeIDFromURL = getSelectedUpgradeIDFromURL();
            dispatch(
                loadConcreteBundles({
                    variantID: nextBaseVariantID,
                    selectedUpgradeID: selectedUpgradeIDFromURL,
                }),
            );
            // Push if variantID is changed
            trackVariantSelectedEvent(nextBaseVariantID);
        }

        // Update the page URL
        if (
            props.enableHistoryInteraction &&
            (baseVariantChanged || upgradedVariantChanged)
        ) {
            pushPDPVariantURLChange(
                rootProduct,
                baseVariant,
                selectedUpgradeID,
                categorySelectors,
            );
            // Refire Listrak AddProductBrowse activity only when the selected variant is changed.
            if (
                prevBaseVariantID &&
                baseVariant &&
                baseVariant.skus.length > 0
            ) {
                trackListrackProductBrowse(baseVariant.skus[0]);
            }
        }

        // Update refs for next render
        prevBaseVariantIDRef.current = nextBaseVariantID;
        prevUpgradedVariantIDRef.current = nextUpgradedVariantID;
    }, [
        baseVariant,
        upgradedVariant,
        props.enableHistoryInteraction,
        rootProduct,
        selectedUpgradeID,
        categorySelectors,
        dispatch,
    ]);

    return (
        <PLCConfiguratorComponent
            preheader={props.preheader}
            subhead={props.subhead}
            description={props.description}
            title={props.title}
            footer={props.footer}
            categorySelectors={categorySelectors}
            optionPanels={optionPanels}
            upgradedVariant={upgradedVariant}
            price={price}
            priceError={priceError}
            selectedAddonPrice={selectedAddonPrice}
            starRatingURL={props.starRatingURL}
            basketLink={props.basketLink}
            configureGiftsLink={props.configureGiftsLink}
            showUpsellModal={props.showUpsellModal}
            getUpsellModalComponentFn={props.getUpsellModalComponentFn}
            showShippingLead={props.showShippingLead}
            shippingDescription={
                shippingMethod ? shippingMethod.description : null
            }
            rootProduct={rootProduct}
            phoneNumber={props.phoneNumber}
            getDeliveryCopy={props.getDeliveryCopy}
            getDeliveryIcon={props.getDeliveryIcon}
        />
    );
};
